import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Radio,
  Search,
  Menu,
  X,
  Shield,
  MapPin,
  Database,
  Zap,
  Activity,
  Globe,
  Volume2,
  VolumeX,
  Bell,
  BellRing,
  History,
  Flame,
} from 'lucide-react';
import { NotificationService } from '../../services/notificationService';
import { CountryCode } from '../../types/alert';
import { COUNTRY_CONFIGS } from '../../services/locationData';

export const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [notificationPerm, setNotificationPerm] = useState<NotificationPermission>('default');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(() => {
    try {
      const stored = localStorage.getItem('sn_selected_country');
      if (stored && (stored in COUNTRY_CONFIGS)) return stored as CountryCode;
    } catch {}
    return 'US';
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setAudioEnabled(NotificationService.isAudioEnabled());
    setNotificationPerm(NotificationService.getPermission());
  }, []);

  const handleCountryChange = (c: CountryCode) => {
    setSelectedCountry(c);
    try {
      localStorage.setItem('sn_selected_country', c);
    } catch {}
    window.dispatchEvent(new CustomEvent('sn_country_changed', { detail: { country: c } }));
  };

  const toggleAudio = () => {
    const next = !audioEnabled;
    setAudioEnabled(next);
    NotificationService.setAudioEnabled(next);
  };

  const handleRequestNotification = async () => {
    const granted = await NotificationService.requestPermission();
    setNotificationPerm(NotificationService.getPermission());
    if (granted) {
      NotificationService.playChime('info');
    }
  };

  // Listen for Ctrl+K or Cmd+K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('global-radar-search');
        if (searchInput) searchInput.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (quickSearch.trim()) {
      navigate(`/alerts?q=${encodeURIComponent(quickSearch.trim())}`);
      setQuickSearch('');
      setMobileMenuOpen(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <header className="sticky top-0 z-50 bg-navy-950/95 backdrop-blur-xl border-b border-slate-800 transition-all text-white">
      {/* Top Telemetry Strip */}
      <div className="bg-slate-950 text-slate-300 text-[10px] font-mono tracking-wider py-1 px-4 border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 overflow-hidden">
            <span className="inline-flex items-center gap-1.5 text-emerald-400 font-semibold uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              NWS &amp; USGS RADAR STREAM ACTIVE
            </span>
            <span className="text-slate-700 hidden sm:inline">|</span>
            <span className="text-slate-400 hidden sm:inline truncate">
              {selectedCountry === 'GLOBAL'
                ? 'MULTI-AGENCY INTERNATIONAL DISRUPTION TELEMETRY'
                : `${COUNTRY_CONFIGS[selectedCountry].name.toUpperCase()} • ${COUNTRY_CONFIGS[selectedCountry].primaryAgency.toUpperCase()}`}
            </span>
          </div>

          <div className="flex items-center gap-2.5 text-slate-400 flex-shrink-0">
            {/* Multi-Country Market Switcher */}
            <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-lg p-0.5">
              {(['US', 'CA', 'GB', 'AU', 'GLOBAL'] as CountryCode[]).map((c) => {
                const conf = COUNTRY_CONFIGS[c];
                return (
                  <button
                    key={c}
                    onClick={() => handleCountryChange(c)}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors flex items-center gap-1 ${
                      selectedCountry === c
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 font-bold'
                        : 'text-slate-400 hover:text-white'
                    }`}
                    title={`Switch radar to ${conf.name}`}
                  >
                    <span>{conf.flag}</span>
                    <span className="hidden sm:inline">{c}</span>
                  </button>
                );
              })}
            </div>

            {/* Audio Emergency Tone Toggle */}
            <button
              onClick={toggleAudio}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono transition-colors ${
                audioEnabled
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                  : 'hover:text-slate-200'
              }`}
              title={audioEnabled ? 'Emergency audio chime enabled' : 'Click to enable audio warning chime'}
            >
              {audioEnabled ? <Volume2 className="w-3 h-3 text-cyan-400" /> : <VolumeX className="w-3 h-3 text-slate-500" />}
              <span className="hidden sm:inline">CHIME: {audioEnabled ? 'ON' : 'MUTED'}</span>
            </button>

            {/* Web Push Notification Status */}
            {NotificationService.isSupported() && (
              <button
                onClick={handleRequestNotification}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono transition-colors ${
                  notificationPerm === 'granted'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                    : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                }`}
                title={
                  notificationPerm === 'granted'
                    ? 'Browser alerts active for severe events'
                    : 'Click to enable browser notifications for your saved locations'
                }
              >
                {notificationPerm === 'granted' ? (
                  <BellRing className="w-3 h-3 text-emerald-400" />
                ) : (
                  <Bell className="w-3 h-3" />
                )}
                <span className="hidden sm:inline">
                  {notificationPerm === 'granted' ? 'NOTIFS: ON' : 'ALERTS'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo & Radar Brand */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative flex items-center justify-center w-10 h-10 bg-slate-900 rounded-xl text-blue-400 shadow-md border border-slate-800 group-hover:border-cyan-500/50 transition-all overflow-hidden">
                <Radio className="w-5 h-5 text-cyan-400 relative z-10" />
                {/* Rotating scanner sweep */}
                <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/25 to-transparent animate-radar-sweep origin-center pointer-events-none" />
                <span className="absolute top-1.5 right-1.5 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
              </div>

              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-black tracking-tight text-white font-sans">
                    Signal<span className="text-cyan-400">Near</span>
                  </span>
                  <span className="bg-red-500/20 text-red-400 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border border-red-500/30 tracking-wider">
                    RADAR
                  </span>
                </div>
                <span className="text-[10px] font-mono text-slate-400 tracking-wider uppercase hidden sm:block">
                  Live US Disruption Intelligence
                </span>
              </div>
            </Link>

            {/* Desktop Navigation Links */}
            <nav className="hidden lg:flex items-center gap-1 text-sm font-semibold text-slate-300">
              <Link
                to="/"
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  isActive('/')
                    ? 'text-cyan-400 bg-slate-900 font-bold border border-cyan-500/30'
                    : 'hover:text-white hover:bg-slate-900/60'
                }`}
              >
                National Radar
              </Link>
              <Link
                to="/alerts"
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  isActive('/alerts')
                    ? 'text-cyan-400 bg-slate-900 font-bold border border-cyan-500/30'
                    : 'hover:text-white hover:bg-slate-900/60'
                }`}
              >
                All Bulletins
              </Link>
              <Link
                to="/history"
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                  isActive('/history')
                    ? 'text-cyan-400 bg-slate-900 font-bold border border-cyan-500/30'
                    : 'hover:text-white hover:bg-slate-900/60'
                }`}
              >
                <History className="w-3.5 h-3.5 text-amber-400" />
                <span>24h Cleared</span>
              </Link>
              <Link
                to="/locations"
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  isActive('/locations')
                    ? 'text-cyan-400 bg-slate-900 font-bold border border-cyan-500/30'
                    : 'hover:text-white hover:bg-slate-900/60'
                }`}
              >
                State Radars
              </Link>
              <Link
                to="/saved"
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  isActive('/saved')
                    ? 'text-cyan-400 bg-slate-900 font-bold border border-cyan-500/30'
                    : 'hover:text-white hover:bg-slate-900/60'
                }`}
              >
                Saved Spots
              </Link>
              <Link
                to="/sources"
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  isActive('/sources')
                    ? 'text-cyan-400 bg-slate-900 font-bold border border-cyan-500/30'
                    : 'hover:text-white hover:bg-slate-900/60'
                }`}
              >
                Data Pipeline
              </Link>
            </nav>
          </div>

          {/* Quick Search Header with Cmd+K hint */}
          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="relative w-60 xl:w-68">
              <input
                id="global-radar-search"
                type="text"
                placeholder="Search state, storm, county..."
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                className="w-full pl-9 pr-12 py-1.5 text-xs bg-slate-900 border border-slate-700/80 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:bg-slate-900 text-white placeholder:text-slate-500 font-sans transition-all"
              />
              <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-2.5 pointer-events-none" />
              <div className="absolute right-2 top-2 pointer-events-none flex items-center">
                <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[9px] font-mono text-slate-400 bg-slate-800 border border-slate-700 rounded">
                  ⌘K
                </kbd>
              </div>
            </form>

            <Link
              to="/alerts?severity=Extreme"
              className="px-3 py-1.5 text-xs font-bold text-red-300 bg-red-950/60 hover:bg-red-900/80 border border-red-700/60 rounded-lg transition-colors flex items-center gap-1.5 shadow-2xs"
            >
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span>Extreme</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-xl text-slate-200 hover:bg-slate-800 focus:outline-none"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-800 bg-navy-950/98 backdrop-blur-xl px-4 pt-3 pb-5 space-y-3 shadow-xl">
          <form onSubmit={handleSearchSubmit} className="relative">
            <input
              type="text"
              placeholder="Search state, county, storm..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-900 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white"
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
          </form>

          <nav className="flex flex-col space-y-1 text-sm font-semibold text-slate-300">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-900"
            >
              National Radar Overview
            </Link>
            <Link
              to="/alerts"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-900"
            >
              All Active Bulletins
            </Link>
            <Link
              to="/history"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-900 text-amber-400 flex items-center gap-2"
            >
              <History className="w-4 h-4" />
              <span>24h Cleared Storm Archive</span>
            </Link>
            <Link
              to="/locations"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-900"
            >
              States & Locations Directory
            </Link>
            <Link
              to="/saved"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-900"
            >
              Saved Locations (Family Dashboard)
            </Link>
            <Link
              to="/sources"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-900"
            >
              Verified Data Pipeline
            </Link>
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-900"
            >
              About & Methodology
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
